import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import LoginRegister from '@/components/LoginRegister';
import RestaurantDashboard from '@/components/RestaurantDashboard';
import SupplierDashboard from '@/components/SupplierDashboard';
import AuthCallback from '@/components/AuthCallback';
import Onboarding from '@/components/Onboarding';
import { PaymentSuccess, PaymentCancel } from '@/components/PaymentResult';
import SellerProfile from '@/components/SellerProfile';
import Integrations from '@/components/Integrations';
import LegalPage from '@/components/LegalPage';
import DeleteAccountPage from '@/components/DeleteAccountPage';
import CookieBanner from '@/components/CookieBanner';
import AutoTranslate from '@/components/AutoTranslate';
import AdminDashboard from '@/components/AdminDashboard';
import '@/i18n';

const ProtectedRoute = ({ children, allowIncomplete = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  // Force incomplete Google users to finish onboarding first
  if (!allowIncomplete && user.profile_complete === false) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (user.profile_complete === false) return <Navigate to="/onboarding" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Only admins may render the admin panel; everyone else is bounced to their dashboard.
const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user?.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
};

const DashboardRouter = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'restaurant' || user.role === 'shop') return <RestaurantDashboard />;
  if (user.role === 'supplier' || user.role === 'manufacturer') return <SupplierDashboard />;
  return <Navigate to="/onboarding" replace />;
};

const AppRouter = () => {
  const location = useLocation();
  // Detect Google auth callback synchronously during render — prevents race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/proveedor/:sellerId" element={<SellerProfile />} />
      <Route path="/privacidad" element={<LegalPage kind="privacy" />} />
      <Route path="/cookies" element={<LegalPage kind="cookies" />} />
      <Route path="/condiciones" element={<LegalPage kind="terms" />} />
      <Route path="/eliminar-cuenta" element={<DeleteAccountPage />} />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminRoute><AdminDashboard /></AdminRoute>
        </ProtectedRoute>
      } />
      <Route path="/integraciones" element={
        <ProtectedRoute><Integrations /></ProtectedRoute>
      } />
      <Route path="/login" element={<PublicRoute><LoginRegister /></PublicRoute>} />
      <Route path="/onboarding" element={
        <ProtectedRoute allowIncomplete={true}>
          <Onboarding />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardRouter />
        </ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <CookieBanner />
        <AutoTranslate />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AuthProvider>
  );
}

export default App;
